// UnionDuesHistoryModal.jsx
// Remittance history + annual report for union dues.
//  • Batches tab  – every export / manual record, mark remitted or undo,
//                   re-download the stored Excel files, piecework log
//                   worksheets (PDF zip), audit CSV, delete a batch
//  • Annual tab   – totals by month and by worker for a year, CSV download
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import JSZip from "jszip";
import { format, parseISO } from "date-fns";
import { buildPDF } from "./PieceworkerLogsTab3"; // adjust path if needed
const API = "https://erp-xe9u.onrender.com/api";

const ACCENT = "#f97316";
const money = (n) =>
  (Number(n) || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
const num2 = (n) => (Number(n) || 0).toFixed(2);
const cls = (...c) => c.filter(Boolean).join(" ");
const ymLabel = (ym) => {
  try {
    return format(parseISO(`${ym}-01`), "MMMM yyyy");
  } catch {
    return ym || "—";
  }
};

const inputCls =
  "p-2 rounded-lg bg-[#161a20] border border-[#2a2f37] text-gray-200 focus:ring-1 focus:ring-orange-500 focus:border-orange-500 text-sm outline-none";

const Tag = ({ tint, children }) => (
  <span
    className="inline-block px-2 py-0.5 rounded text-[11px] font-medium"
    style={{ color: tint, backgroundColor: `${tint}1f` }}
  >
    {children}
  </span>
);

const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

const safeName = (s) => (s || "Unknown").replace(/[/\\?%*:|"<>]/g, "-").trim();

/**
 * Props: open, apiBase, getHeaders, onClose, onNotify(type,title,message)
 */
export default function UnionDuesHistoryModal({
  open,
  apiBase,
  getHeaders,
  onClose,
  onNotify,
}) {
  const [tab, setTab] = useState("batches");
  const [year, setYear] = useState(new Date().getFullYear());
  const [includeAll, setIncludeAll] = useState(false);
  const [unionFilter, setUnionFilter] = useState("");
  const [unionList, setUnionList] = useState([]);
  const [batches, setBatches] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [confirmDel, setConfirmDel] = useState(null);

  const base = `${apiBase}/payroll/union-dues/remittances`;

  // The parent passes a fresh getHeaders function on every render. Keep it in
  // a ref so it never retriggers loading (that caused an endless request loop).
  const headersRef = useRef(getHeaders);
  headersRef.current = getHeaders;
  const hdrs = () => headersRef.current();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [b, r] = await Promise.all([
        axios.get(base, {
          headers: hdrs(),
          params: { year, union: unionFilter || undefined },
        }),
        axios.get(`${base}/report`, {
          headers: hdrs(),
          params: {
            year,
            include: includeAll ? "all" : undefined,
            union: unionFilter || undefined,
          },
        }),
      ]);
      setBatches(b.data.remittances || []);
      setReport(r.data);
      setUnionList(r.data.unions || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Load failed");
    } finally {
      setLoading(false);
    }
  }, [base, year, includeAll, unionFilter]);

  // Fetch only when the modal opens or the year / filter changes — never because
  // a prop's identity changed.
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    if (open) loadRef.current();
  }, [open, year, includeAll, unionFilter]);

  useEffect(() => {
    if (!open) setConfirmDel(null);
  }, [open]);

  const setStatus = async (b, status) => {
    setBusy(b._id);
    setError("");
    try {
      await axios.patch(`${base}/${b._id}`, { status }, { headers: hdrs() });
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Update failed");
    } finally {
      setBusy("");
    }
  };

  const remove = async (b) => {
    setBusy(b._id);
    setError("");
    try {
      await axios.delete(`${base}/${b._id}`, { headers: hdrs() });
      setConfirmDel(null);
      await load();
      if (onNotify)
        onNotify(
          "success",
          "Batch Deleted",
          "Its entries can be remitted again.",
        );
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Delete failed");
    } finally {
      setBusy("");
    }
  };

  const downloadFile = async (b, f) => {
    try {
      const res = await axios.get(`${base}/${b._id}/files/${f.kind}`, {
        headers: hdrs(),
        responseType: "blob",
      });
      saveBlob(res.data, f.filename || `union-dues-${f.kind}.xlsx`);
    } catch (err) {
      setError(
        err.response?.status === 404
          ? "File not stored for this batch"
          : "Download failed",
      );
    }
  };

  const downloadAudit = async (b) => {
    try {
      const res = await axios.get(`${base}/${b._id}/audit.csv`, {
        headers: hdrs(),
        responseType: "blob",
      });
      saveBlob(res.data, `Union-Dues-Audit-${b.workMonth}.csv`);
    } catch {
      setError("Audit download failed");
    }
  };

  // Piecework worksheets (PDF) for every piecework entry in the batch.
  // Zip layout: one PDF per worker (one page per log); one folder per project
  // when the batch spans more than one project.
  const downloadPieceworkLogs = async (b) => {
    setBusy(b._id);
    setError("");
    try {
      const res = await axios.get(`${base}/${b._id}/piecework-logs`, {
        headers: hdrs(),
      });
      const logs = res.data.logs || [];
      if (!logs.length) {
        setError("No piecework logs are linked to this batch");
        return;
      }

      // project -> worker -> logs
      const byProject = new Map();
      for (const log of logs) {
        const pKey = log.project?._id || "none";
        if (!byProject.has(pKey))
          byProject.set(pKey, { project: log.project, workers: new Map() });
        const wKey = log.worker?._id || "unknown";
        const workers = byProject.get(pKey).workers;
        if (!workers.has(wKey))
          workers.set(wKey, {
            name: log.worker?.displayName || log.worker?.email || "Unknown",
            logs: [],
          });
        workers.get(wKey).logs.push(log);
      }

      const zip = new JSZip();
      const multiProject = byProject.size > 1;
      for (const { project, workers } of byProject.values()) {
        const folder = multiProject
          ? zip.folder(safeName(project?.projectName || project?.projectNumber))
          : zip;
        for (const { name, logs: wLogs } of workers.values()) {
          wLogs.sort(
            (a, c) =>
              new Date(a.workCompletedDate || 0) -
              new Date(c.workCompletedDate || 0),
          );
          const doc = buildPDF(wLogs, project);
          folder.file(`${safeName(name)}.pdf`, doc.output("arraybuffer"));
        }
      }

      const blob = await zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: { level: 6 },
      });
      saveBlob(blob, `Union-Dues-Piecework-Logs-${b.workMonth}.zip`);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Piecework logs download failed",
      );
    } finally {
      setBusy("");
    }
  };

  const downloadCsv = () => {
    if (!report) return;
    const rows = [
      [
        `Union dues ${report.year}${report.union ? " — " + report.union : ""}`,
        report.include === "all" ? "(incl. prepared)" : "(remitted only)",
      ],
      [],
      [
        "Work month",
        "Workers",
        "Hourly hrs",
        "Hourly gross",
        "Hourly dues",
        "Piece gross",
        "Piece dues",
        "Total gross",
        "Total dues",
        "Remitted",
      ],
      ...report.months.map((m) => [
        m.workMonth,
        m.workers,
        num2(m.hourlyHours),
        num2(m.hourlyGross),
        num2(m.hourlyDues),
        num2(m.pieceGross),
        num2(m.pieceDues),
        num2(m.totalGross),
        num2(m.totalDues),
        m.allRemitted ? "Yes" : "Partly",
      ]),
      [
        "TOTAL",
        "",
        num2(report.totals.hourlyHours),
        "",
        "",
        "",
        "",
        num2(report.totals.totalGross),
        num2(report.totals.totalDues),
        "",
      ],
      [],
      [
        "Worker",
        "Months",
        "Hours",
        "Hourly gross",
        "Piece gross",
        "Total gross",
        "Dues",
      ],
      ...report.workers.map((w) => [
        w.name,
        w.months,
        num2(w.hours),
        num2(w.hourlyGross),
        num2(w.pieceGross),
        num2(w.totalGross),
        num2(w.dues),
      ]),
    ];
    const csv = rows.map((r) => r.map(csvCell).join(",")).join("\r\n");
    saveBlob(
      new Blob([csv], { type: "text/csv;charset=utf-8" }),
      `Union-Dues-${report.year}.csv`,
    );
  };

  const years = useMemo(() => {
    const y = new Date().getFullYear();
    return [y + 1, y, y - 1, y - 2, y - 3, y - 4];
  }, []);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[70] px-4">
      <div
        className="rounded-xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col"
        style={{ backgroundColor: "#12151b", border: "1px solid #2a2f37" }}
      >
        <div className="px-6 pt-5 pb-3 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold text-gray-100">
              Union Dues Remittances
            </h2>
            <p className="text-xs text-gray-500">
              What was sent to the union, month by month
            </p>
          </div>
          <div className="flex items-center gap-2">
            {unionList.length > 1 && (
              <select
                value={unionFilter}
                onChange={(e) => setUnionFilter(e.target.value)}
                className={inputCls}
              >
                <option value="">All unions</option>
                {unionList.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
            )}
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value, 10))}
              className={inputCls}
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <button
              onClick={onClose}
              className="px-3 py-2 text-sm text-gray-300 bg-[#1c2027] rounded-lg hover:bg-[#22262e]"
            >
              Close
            </button>
          </div>
        </div>

        <div className="px-6 flex gap-1 border-b border-[#2a2f37]">
          {[
            ["batches", "Batches"],
            ["annual", "Annual report"],
          ].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={cls(
                "px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors",
                tab === k
                  ? "text-orange-400 border-orange-500"
                  : "text-gray-400 border-transparent hover:text-gray-200",
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {error && (
          <div className="mx-6 mt-3 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="px-6 py-4 overflow-auto flex-1 min-h-[240px]">
          {loading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : tab === "batches" ? (
            batches.length === 0 ? (
              <p className="text-sm text-gray-500">
                No remittances recorded for {year}. Exports are saved here
                automatically; tick “Mark these entries as remitted” when you
                send one.
              </p>
            ) : (
              <table className="min-w-full text-sm">
                <thead className="text-[11px] uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="py-2 pr-3 text-left">Work month</th>
                    <th className="py-2 pr-3 text-left">Status</th>
                    <th className="py-2 pr-3 text-right">Workers</th>
                    <th className="py-2 pr-3 text-right">Gross</th>
                    <th className="py-2 pr-3 text-right">Dues</th>
                    <th className="py-2 pr-3 text-left">Reference</th>
                    <th className="py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map((b) => {
                    const t = b.totals || {};
                    const done = b.status === "remitted";
                    const hasPiecework =
                      (b.files || []).some((f) => f.kind === "piecework") ||
                      (t.pieceGross || 0) > 0;
                    return (
                      <tr
                        key={b._id}
                        className="border-t border-[#1c2027] align-top"
                      >
                        <td className="py-2 pr-3 text-gray-200 whitespace-nowrap">
                          {ymLabel(b.workMonth)}
                          {b.union && (
                            <span className="ml-2 text-[11px] text-orange-400">
                              {b.union}
                            </span>
                          )}
                          <div className="text-[11px] text-gray-500">
                            {b.source === "manual" ? "Recorded" : "Exported"}{" "}
                            {b.createdAt
                              ? format(new Date(b.createdAt), "MMM d, yyyy")
                              : ""}
                            {b.remitMonth ? ` · remit ${b.remitMonth}` : ""}
                          </div>
                        </td>
                        <td className="py-2 pr-3">
                          <Tag tint={done ? "#34d399" : "#60a5fa"}>
                            {done ? "Remitted" : "Prepared"}
                          </Tag>
                          {done && b.remittedAt && (
                            <div className="text-[11px] text-gray-500 mt-1">
                              {format(new Date(b.remittedAt), "MMM d, yyyy")}
                            </div>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-right text-gray-300">
                          {t.workers || 0}
                        </td>
                        <td className="py-2 pr-3 text-right font-mono text-xs text-gray-200">
                          {money((t.hourlyGross || 0) + (t.pieceGross || 0))}
                        </td>
                        <td className="py-2 pr-3 text-right font-mono text-xs text-gray-200">
                          {money((t.hourlyDues || 0) + (t.pieceDues || 0))}
                        </td>
                        <td className="py-2 pr-3 text-gray-400 text-xs">
                          {b.reference || "—"}
                        </td>
                        <td className="py-2 text-right whitespace-nowrap">
                          <div className="flex flex-wrap gap-1 justify-end">
                            {(b.files || []).map((f) => (
                              <button
                                key={f.kind}
                                onClick={() => downloadFile(b, f)}
                                className="px-2 py-1 text-xs text-gray-300 bg-[#1c2027] rounded hover:bg-[#22262e]"
                              >
                                {f.kind === "hourly"
                                  ? "ICI .xlsx"
                                  : "Piecework .xlsx"}
                              </button>
                            ))}
                            <button
                              onClick={() => downloadAudit(b)}
                              title="Every payroll line with its timesheet / piecework log reference"
                              className="px-2 py-1 text-xs text-gray-300 bg-[#1c2027] rounded hover:bg-[#22262e]"
                            >
                              Audit .csv
                            </button>
                            {hasPiecework && (
                              <button
                                disabled={busy === b._id}
                                onClick={() => downloadPieceworkLogs(b)}
                                title="Worker piecework worksheets (PDF) for every piecework entry in this batch"
                                className="px-2 py-1 text-xs text-gray-300 bg-[#1c2027] rounded hover:bg-[#22262e] disabled:opacity-40"
                              >
                                {busy === b._id
                                  ? "Preparing…"
                                  : "Piecework logs .zip"}
                              </button>
                            )}
                            <button
                              disabled={busy === b._id}
                              onClick={() =>
                                setStatus(b, done ? "prepared" : "remitted")
                              }
                              className={cls(
                                "px-2 py-1 text-xs rounded disabled:opacity-40",
                                done
                                  ? "text-gray-300 bg-[#1c2027] hover:bg-[#22262e]"
                                  : "text-white hover:brightness-110",
                              )}
                              style={
                                done ? undefined : { backgroundColor: ACCENT }
                              }
                            >
                              {done ? "Undo remitted" : "Mark remitted"}
                            </button>
                            {confirmDel === b._id ? (
                              <button
                                disabled={busy === b._id}
                                onClick={() => remove(b)}
                                className="px-2 py-1 text-xs text-white bg-red-600 rounded hover:bg-red-500 disabled:opacity-40"
                              >
                                Confirm delete
                              </button>
                            ) : (
                              <button
                                onClick={() => setConfirmDel(b._id)}
                                className="px-2 py-1 text-xs text-red-300 bg-[#1c2027] rounded hover:bg-[#22262e]"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )
          ) : (
            <div>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={includeAll}
                    onChange={(e) => setIncludeAll(e.target.checked)}
                    className="h-4 w-4 accent-orange-500 rounded"
                  />
                  Include prepared (not yet remitted) batches
                </label>
                <button
                  onClick={downloadCsv}
                  disabled={!report || report.months.length === 0}
                  className="px-3 py-2 text-sm font-medium text-white rounded-lg hover:brightness-110 disabled:opacity-40"
                  style={{ backgroundColor: ACCENT }}
                >
                  Download CSV
                </button>
              </div>
              {!report || report.months.length === 0 ? (
                <p className="text-sm text-gray-500">
                  Nothing {includeAll ? "recorded" : "remitted"} for {year} yet.
                </p>
              ) : (
                <>
                  <table className="min-w-full text-sm mb-6">
                    <thead className="text-[11px] uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="py-2 pr-3 text-left">Month</th>
                        <th className="py-2 pr-3 text-right">Workers</th>
                        <th className="py-2 pr-3 text-right">Hours</th>
                        <th className="py-2 pr-3 text-right">Hourly gross</th>
                        <th className="py-2 pr-3 text-right">Piece gross</th>
                        <th className="py-2 pr-3 text-right">Dues</th>
                        <th className="py-2 text-left">State</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.months.map((m) => (
                        <tr
                          key={m.workMonth}
                          className="border-t border-[#1c2027]"
                        >
                          <td className="py-2 pr-3 text-gray-200">
                            {ymLabel(m.workMonth)}
                          </td>
                          <td className="py-2 pr-3 text-right text-gray-300">
                            {m.workers}
                          </td>
                          <td className="py-2 pr-3 text-right font-mono text-xs text-gray-300">
                            {num2(m.hourlyHours)}
                          </td>
                          <td className="py-2 pr-3 text-right font-mono text-xs text-gray-300">
                            {money(m.hourlyGross)}
                          </td>
                          <td className="py-2 pr-3 text-right font-mono text-xs text-gray-300">
                            {money(m.pieceGross)}
                          </td>
                          <td className="py-2 pr-3 text-right font-mono text-xs text-gray-100">
                            {money(m.totalDues)}
                          </td>
                          <td className="py-2">
                            <Tag tint={m.allRemitted ? "#34d399" : "#60a5fa"}>
                              {m.allRemitted ? "Remitted" : "Incl. prepared"}
                            </Tag>
                          </td>
                        </tr>
                      ))}
                      <tr className="border-t border-[#2a2f37] font-semibold">
                        <td className="py-2 pr-3 text-gray-200">
                          Total {report.year}
                        </td>
                        <td />
                        <td className="py-2 pr-3 text-right font-mono text-xs text-gray-100">
                          {num2(report.totals.hourlyHours)}
                        </td>
                        <td />
                        <td
                          className="py-2 pr-3 text-right font-mono text-xs text-gray-100"
                          colSpan={1}
                        >
                          {money(report.totals.totalGross)}
                        </td>
                        <td
                          className="py-2 pr-3 text-right font-mono text-xs"
                          style={{ color: ACCENT }}
                        >
                          {money(report.totals.totalDues)}
                        </td>
                        <td />
                      </tr>
                    </tbody>
                  </table>

                  <h3 className="text-sm font-semibold text-gray-200 mb-2">
                    By worker
                  </h3>
                  <table className="min-w-full text-sm">
                    <thead className="text-[11px] uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="py-2 pr-3 text-left">Worker</th>
                        <th className="py-2 pr-3 text-right">Months</th>
                        <th className="py-2 pr-3 text-right">Hours</th>
                        <th className="py-2 pr-3 text-right">Gross</th>
                        <th className="py-2 text-right">Dues</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.workers.map((w) => (
                        <tr key={w.name} className="border-t border-[#1c2027]">
                          <td className="py-2 pr-3 text-gray-200">{w.name}</td>
                          <td className="py-2 pr-3 text-right text-gray-300">
                            {w.months}
                          </td>
                          <td className="py-2 pr-3 text-right font-mono text-xs text-gray-300">
                            {num2(w.hours)}
                          </td>
                          <td className="py-2 pr-3 text-right font-mono text-xs text-gray-300">
                            {money(w.totalGross)}
                          </td>
                          <td className="py-2 text-right font-mono text-xs text-gray-100">
                            {money(w.dues)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
